import React, { useState } from "react";
import { Typography, Box, Grid2, ListItemText, useTheme, Skeleton, ListItem, ListItemIcon, List } from "@mui/material";
import styles from "../../../styles/ProjectList.module.css";
import Highcharts from "highcharts/highstock";
import PieChart from "highcharts-react-official";
import { RxSize } from "react-icons/rx";
import { useGetIssuesDistributionModuleQuery } from '../../../store/slices/apis/issuesDashboardSlice';
import IssuesPopup from "./IssuesPopup";
import { useParams } from "react-router-dom";
import { FaSquareFull } from "react-icons/fa6";


const IssuesDistributionModuleGraph = () => {
  const { projectId } = useParams();
  const [popupData, setPopupData] = useState(null);
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const { data: IssuesDistributionModule = [], isLoading } = useGetIssuesDistributionModuleQuery({
    projectId: projectId
  });

  const moduleColorMap = {
    'P&M': '#3F766E',
    'Manpower': '#E72B33',
    'Procurement': '#FDCA40',
  };
  const distributionData = IssuesDistributionModule?.data?.map((item, index) => ({
    y: item.count,
    value: item.count,
    color: moduleColorMap[item.module],
    name: item.module,
  })) || [];

  const options = {
    chart: {
      type: "pie",
      width: 200,
      height: 200,
      marginBottom: 30,
      marginTop: 0,
      marginRight: 0,
    },
    caption: {
      useHTML: true,
      text: '',
      //text: '<div style="width: 68px; height: 68px; background-color: white; border-radius: 50%;padding: 25px 10px 25px 10px; border: 1px solid #f3f3f3;">72%</div>',
      verticalAlign: 'middle',
      style: { "fontSize": "20px", "fontWeight": "600", "color": "#000000", "lineHeight": "100%" },
      y: 100, x: 20,
    },
    title: {
      useHTML: true,
      text: '',
      //text: '<div style="width: 68px; height: 68px; background-color: white; border-radius: 50%;padding: 25px 10px 25px 10px; border: 1px solid #f3f3f3;">8%</div>',
      verticalAlign: 'middle',
      style: { "fontSize": "20px", "fontWeight": "600", "color": "#000000", "lineHeight": "100%" },
      y: -65, x: -82,
    },
    subtitle: {
      useHTML: true,
      text: '',
      //text: '<div style="width: 68px; height: 68px; background-color: white; border-radius: 50%;padding: 25px 10px 25px 10px; border: 1px solid #f3f3f3;">20%</div>',
      verticalAlign: 'middle',
      style: { "fontSize": "20px", "fontWeight": "600", "color": "#000000", "lineHeight": "100%" },
      y: -65, x: 82,
    },
    credits: {
      enabled: false
    },
    tooltip: {
      formatter: function () {
        // Customize the hover text here
        return `<b style='color: ${this.color}'>${this.name}</b>: ${this.y}`;
      }
    },
    plotOptions: {
      pie: {
        allowPointSelect: false,
        borderWidth: 0,
        borderRadius: 0,
        cursor: "pointer",
        dataLabels: {
          enabled: false,
        },
        showInLegend: false,
        innerSize: 80,
        //startAngle: -20,
        point: {
          events: {
            click: function () {
              setPopupData({
                name: this.name,
                value: this.y,
              });
              setOpen(true);
            }
          }
        },
      }
    },
    series: [
      {
        name: "",
        dataLabels: {
          enabled: false,
        },
        color: "#006600",
        lineWidth: 1,
        marker: {
          enabled: false,
          symbol: "circle",
          radius: 3,
          states: {
            hover: {
              enabled: true,
              lineWidth: 1
            }
          }
        },
        data: distributionData,
      }
    ]
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2, width: "100%", height: "251px", maxWidth: "100%", border: "1px solid #D8D8D8", mt: 0, borderRadius: "12px", overflow: "hidden", backgroundColor: "white" }}>
        <Box sx={{}}>
          <Typography variant="body" sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "100%", letterSpacing: '-0.4px', color: `${theme?.customSettings?.themeColor}`, }}>
            Issues Distribution by Module
          </Typography>
          <Typography sx={{ mt: "5px", color: "#5052588A", fontWeight: 500, fontSize: "14px", lineHeight: "100%", letterSpacing: '-0.4px' }}>
            Issues by module to identify focus areas for resolution and quality improvement
          </Typography>
        </Box>

        <Grid2 container>
          <Grid2 size={{ xs: 12, md: 7 }}>
            {[...Array(3)].map((_, index) => (
              <Grid2 container direction="row" spacing={1} key={index} sx={{ marginLeft: "10px", paddingTop: index === 0 ? '40px' : '10px' }}>
                <Box sx={{ width: "25px", padding: "8px" }}>
                  <Skeleton variant="circular" width={18} height={18} />
                </Box>
                <Box sx={{ width: "130px", padding: "8px" }}>
                  <Skeleton variant="text" width={100} height={20} />
                </Box>
                <Box sx={{ width: "30px", padding: "4px" }}>
                  <Skeleton variant="text" width={20} height={28} />
                </Box>
              </Grid2>
            ))}
          </Grid2>

          <Grid2 size={{ xs: 12, md: 5 }} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Skeleton variant="circular" width={120} height={120} />
          </Grid2>
        </Grid2>
      </Box>
    );
  }


  return (
    <Box sx={{ p: 2, width: "100%", height: "251px", maxWidth: "100%", border: "1px solid #D8D8D8", mt: 0, borderRadius: "12px", overflow: "hidden", backgroundColor: "white" }}>
      <Box sx={{}}>
        <Typography variant="body" sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "100%", letterSpacing: '-0.4px', color: `${theme?.customSettings?.themeColor}`, }}>
          Issues Distribution by Module
        </Typography>
        <Typography sx={{ mt: "5px", color: "#5052588A", fontWeight: 500, fontSize: "14px", lineHeight: "100%", letterSpacing: '-0.4px' }}>
          Issues by module to identify focus areas for resolution and quality improvement
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", }}>
        {distributionData?.length === 0 ? (
          <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="textSecondary">
              No data available to display the chart.
            </Typography>
          </Box>
        ) : (
          <>
            <List sx={{ padding: 0, mb: 4 }}>
              {distributionData?.map((val, index) => (
                <ListItem key={index} sx={{ paddingLeft: 0 }}>
                  <ListItemIcon sx={{ minWidth: '20px' }}>
                    <FaSquareFull
                      style={{ color: `${val?.color}`, height: '14px', width: '14px' }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        <Typography component="span" sx={{ fontWeight: 500, fontSize: "16px", letterSpacing: "-0.4px" }}>
                          {val?.name}:{' '}
                        </Typography>
                        <Typography component="span" sx={{ fontWeight: 500, fontSize: "16px", color: "#182A4E" }}>
                          {val?.value}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
            <Box sx={{ flexShrink: 0 }}>
              <PieChart highcharts={Highcharts} options={options} />
            </Box>
          </>
        )}
      </Box>
      {open && (() => {
        return (
          <IssuesPopup title={'Issues Distribution Module'} value={popupData.name} status={'Module'} onOpen={open} onClose={() => setOpen(false)} />
        )
      })()}
    </Box>

  );
};

export default IssuesDistributionModuleGraph;